import { Box, Heading, SimpleGrid, useToast } from '@chakra-ui/react';
import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import debounce from 'lodash.debounce';
import { isBefore, subDays } from 'date-fns';
import { DefaultLayout } from '../../../_layout/DefaultLayout';
import { maskDateTime } from '../../../../../utils/formatters/handleMask';
import { listBookingsService } from '../../../../../services/Bookings/ListBookingsService';
import { translateError } from '../../../../../utils/errors';
import { IBookingTableItem } from '../../../../../components/BookingTable';
import { useAuth } from '../../../../../hooks/auth';
import {
  BookingNoShowTable,
  IBookingNoShowTableItemCheckin,
} from './components/BookingNoShowTable';
import DatePickerBase from '../../../../../components/Form/DatePickerBase';

interface ILoadBookingsProps {
  activityName?: string;
  bookedDate?: string;
  spotName?: string;
  userDocument?: string;
  userName?: string;
  maxBookedDate?: string;
  minBookedDate?: string;
  sort?: 'bookedDate' | 'createdAt';
  order?: 'ASC' | 'DESC';
  page?: number;
}

export const BookingNoShowList = (): JSX.Element => {
  const toast = useToast();

  const { venture: authenticatedVenture } = useAuth();

  const [bookingsList, setBookingsList] = useState<IBookingTableItem[]>([]);
  const [currentMinBookedDate, setCurrentMinBookedDate] = useState<
    Date | undefined
  >(subDays(new Date(), 1));
  const [currentMaxBookedDate, setCurrentMaxBookedDate] = useState<
    Date | undefined
  >(new Date());
  const [currentFilters, setCurrentFilters] = useState<
    | {
        [key: string]: string;
      }
    | undefined
  >(() => {
    const response: {
      [key: string]: string;
    } = {};

    if (currentMaxBookedDate) {
      response.maxBookedDate =
        currentMaxBookedDate?.toLocaleDateString('en-CA');
    }

    if (currentMinBookedDate) {
      response.minBookedDate =
        currentMinBookedDate?.toLocaleDateString('en-CA');
    }

    return response;
  });

  const [currentOrdering, setCurrentOrdering] = useState<'ASC' | 'DESC'>(
    'DESC',
  );
  const [currentPage, setCurrentPage] = useState(1);
  const [filterBy, setFilterBy] = useState('userName');
  const [sortBy, setSortBy] = useState<'bookedDate' | 'createdAt'>(
    'bookedDate',
  );
  const [totalPages, setTotalPages] = useState<number>();

  const loadBookings = useCallback(
    async ({
      activityName,
      bookedDate,
      spotName,
      userDocument,
      userName,
      order,
      maxBookedDate,
      minBookedDate,
      page,
      sort = 'bookedDate',
    }: ILoadBookingsProps) => {
      try {
        const bookings = await listBookingsService({
          activityName,
          bookedDate,
          spotName,
          userDocument: userDocument?.replace(/\D/g, ''),
          userName,
          maxBookedDate,
          minBookedDate,
          noShowOnly: true,
          limit: 20,
          page,
          sort,
          order,
        });

        const parsedBookings = bookings.items.map((booking) => {
          const [bookedYear, bookedMonth, bookedDay] =
            booking.bookedDate.split('-');

          const bookedDateTime = new Date(
            booking?.bookedDate.concat(
              `T${booking.activitySchedule.startTime}`,
            ) || '',
          );

          const isCancelable =
            !booking?.checkins.length &&
            isBefore(new Date(), bookedDateTime) &&
            !booking?.deletedAt;

          return {
            ...booking,
            checkins: booking.checkins.map((checkin) => ({
              ...checkin,
              formattedCreatedAt: maskDateTime(checkin.createdAt),
            })),
            formattedBookedDate: `${bookedDay}/${bookedMonth}/${bookedYear}`,
            formattedCreatedAt: maskDateTime(booking.createdAt),
            formattedDeletedAt: booking.deletedAt
              ? maskDateTime(booking.deletedAt)
              : undefined,
            formattedEndTime: booking.endTime
              ? booking.endTime.slice(11, 16)
              : booking.activitySchedule.endTime.slice(0, 5),
            formattedStartTime: booking.startTime
              ? booking.startTime.slice(11, 16)
              : booking.activitySchedule.startTime.slice(0, 5),
            isCancelable,
          };
        });

        setTotalPages(bookings.pages);
        setBookingsList(parsedBookings);
      } catch (err) {
        if (axios.isAxiosError(err) && err.response?.status !== 401) {
          toast({
            title: 'Falha ao carregar dados',
            description:
              translateError({ message: err.response?.data.message }) ||
              'Ocorreu um erro ao carregar os dados das reservas, tente novamente.',
            status: 'error',
            duration: 3000,
            isClosable: true,
            variant: 'subtle',
            position: 'top-right',
          });
        }
      }
    },
    [toast],
  );

  useEffect(() => {
    setTotalPages(1);
    setBookingsList([]);

    loadBookings({
      ...currentFilters,
      maxBookedDate: currentMaxBookedDate?.toLocaleDateString('en-CA'),
      minBookedDate: currentMinBookedDate?.toLocaleDateString('en-CA'),
      order: currentOrdering,
      page: currentPage,
      sort: sortBy,
    });
  }, [
    authenticatedVenture,
    currentFilters,
    currentMaxBookedDate,
    currentMinBookedDate,
    currentOrdering,
    currentPage,
    loadBookings,
    sortBy,
  ]);

  const handleFilterBy = useCallback((value: string) => {
    setFilterBy(value);
  }, []);

  const handleFilter = useCallback(
    (value: string) => {
      setCurrentFilters(value ? { [filterBy]: value } : undefined);
    },
    [filterBy],
  );

  const debounceHandleFilter = debounce(handleFilter, 1000);

  const handleCheckin = (
    bookingCheckin: IBookingNoShowTableItemCheckin,
  ): void => {
    setBookingsList((prevState) =>
      prevState.filter((booking) => booking.id !== bookingCheckin.bookingId),
    );
  };

  const handleSortList = useCallback((sort: 'bookedDate' | 'createdAt') => {
    setSortBy(sort);
  }, []);

  const handleOrderList = useCallback((order: 'ASC' | 'DESC') => {
    setCurrentOrdering(order);
  }, []);

  const handlePageChange = useCallback((page: number) => {
    setCurrentPage(page);
  }, []);

  return (
    <DefaultLayout>
      <Box flex="1" borderRadius={8} bg="white" p="8">
        <Heading size="lg" fontWeight="normal">
          Não comparecimentos
        </Heading>

        <SimpleGrid mt="4" minChildWidth="120px" spacing="4" maxW={320}>
          <DatePickerBase
            size="sm"
            name="booked-since"
            label="Reservado de"
            maxDate={currentMaxBookedDate}
            isClearable
            selectsStart
            startDate={currentMinBookedDate}
            endDate={currentMaxBookedDate}
            onChangeDate={setCurrentMinBookedDate}
            selectedDate={currentMinBookedDate}
          />

          <DatePickerBase
            size="sm"
            name="booked-until"
            label="Reservado até"
            maxDate={new Date()}
            isClearable
            selectsEnd
            startDate={currentMinBookedDate}
            endDate={currentMaxBookedDate}
            onChangeDate={setCurrentMaxBookedDate}
            selectedDate={currentMaxBookedDate}
          />
        </SimpleGrid>

        <BookingNoShowTable
          mt="4"
          bookings={bookingsList}
          currentPage={currentPage}
          filterBy={filterBy}
          onChangeFilterBy={handleFilterBy}
          onCheckin={handleCheckin}
          onFilter={debounceHandleFilter}
          onOrder={handleOrderList}
          onPageChange={handlePageChange}
          onSort={handleSortList}
          order={currentOrdering}
          sortBy={sortBy}
          totalPages={totalPages}
        />
      </Box>
    </DefaultLayout>
  );
};
